import React from 'react';
import { ChevronLeft, Menu } from 'lucide-react';
import { useBackHandler } from '../../contexts/NavigationBackContext';
import { useCurrentBottomBarActions } from '../../contexts/MobileBottomBarActionsContext';

/**
 * MobileBottomBar (FT-358 icon-tab layout)
 *
 * Single-row fixed bottom bar:
 *
 *   [ 80px Back ][ flex-1 middle: 0-3 module actions ][ 80px Menu ]
 *
 * - Back  : locked left, hidden on Dashboard (no back handler registered).
 *           Icon-only-with-label; fires the topmost handler from
 *           NavigationBackContext (FT-356).
 * - Middle: 0-3 module actions provided via MobileBottomBarActionsContext
 *           (FT-358). Modules call `useSetBottomBarActions([...])` to push
 *           their list; the bar reads the topmost non-empty list.
 * - Menu  : locked right, always visible. Dispatches `shell:openSidebar`
 *           on the window; the shell listens and opens the sidebar drawer
 *           (same event contract as the prior bar — untouched).
 *
 * Each slot is stacked icon (24 px) + tiny label (11 px). Primary actions
 * render as a filled `bg-cyan-600` circle (44 px) with a white icon inside;
 * every existing CTA (Submit / Save) in the app already uses `cyan-600`, so
 * this reuses the live primary token verbatim — no colour invention
 * (feedback_app_colours_are_jonathans_domain).
 *
 * The `<footer>` carries `data-role="mobile-bottom-bar"` so the FT-358
 * double-mount regression test (`mobileBottomBar.doubleMount.test.js`) can
 * assert exactly one bar exists in the DOM.
 */

/**
 * BarSlot — one tab-column inside the bar. Extracted as a local component
 * per brief §7 so all three slots (Back / middle actions / Menu) share the
 * same visual grammar (icon + tiny label, stacked, centered).
 */
function BarSlot({
  icon: Icon,
  label,
  onClick,
  disabled = false,
  primary = false,
  widthClass = '',
  testId,
  ariaLabel,
}) {
  const iconNode = primary ? (
    <span
      className="w-11 h-11 rounded-full bg-cyan-600 flex items-center justify-center"
      data-role="primary-wrapper"
      data-testid={testId ? `${testId}-primary-wrapper` : undefined}
    >
      <Icon className="w-6 h-6 text-white" data-testid={testId ? `${testId}-icon` : undefined} />
    </span>
  ) : (
    <Icon className="w-6 h-6 text-slate-700" data-testid={testId ? `${testId}-icon` : undefined} />
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      aria-label={ariaLabel || label}
      className={`${widthClass} flex flex-col items-center justify-center gap-1 min-h-[64px] ${
        disabled ? 'opacity-40' : 'active:bg-slate-100'
      }`.trim()}
    >
      {iconNode}
      <span className="text-[11px] leading-none text-slate-700 truncate max-w-full">
        {label}
      </span>
    </button>
  );
}

/**
 * MobileBottomBar — single instance mounted by TailwindShell.
 *
 * `isDashboard` is inferred from NavigationBackContext: the Dashboard view
 * doesn't register a back handler, so `onBack` is null and the Back slot
 * naturally hides. Every other module wires `useSetBackHandler(...)` and the
 * Back slot appears. No new prop plumbing invented (per brief §D "carry the
 * existing pattern from OLD bar").
 */
/** LEFT slot: back button, or a fixed spacer on Dashboard (no handler). */
function BackSlot({ onBack }) {
  if (typeof onBack !== 'function') {
    return <div className="w-20" aria-hidden="true" />;
  }
  return (
    <BarSlot
      icon={ChevronLeft}
      label="Back"
      onClick={onBack}
      widthClass="w-20"
      testId="mobile-bottom-bar-back"
    />
  );
}

/** MIDDLE slot: 0-3 module actions, evenly distributed. */
function MiddleSlots({ actions }) {
  return (
    <div
      className="flex-1 flex items-stretch justify-around"
      data-testid="mobile-bottom-bar-actions"
    >
      {actions.map((a, idx) => (
        <BarSlot
          key={a.key || `${a.label}-${idx}`}
          icon={a.icon}
          label={a.label}
          onClick={a.onClick}
          disabled={a.disabled}
          primary={a.primary}
          widthClass="flex-1 max-w-[96px]"
          testId={`mobile-bottom-bar-action-${a.key || a.label}`}
        />
      ))}
    </div>
  );
}

/** RIGHT slot: always-visible Menu button. Fires the shell:openSidebar event
 *  (same contract as the prior bar — untouched). */
function MenuSlot() {
  const openMenu = () =>
    window.dispatchEvent(new CustomEvent('shell:openSidebar'));
  return (
    <BarSlot
      icon={Menu}
      label="Menu"
      onClick={openMenu}
      widthClass="w-20"
      testId="mobile-bottom-bar-menu"
    />
  );
}

/**
 * MobileBottomBar reads everything it needs from context (NavigationBackContext
 * + MobileBottomBarActionsContext) and takes NO props, so it is wrapped in
 * React.memo.
 *
 * FT-404: The bar is mounted by TailwindShell, which itself consumes several
 * high-churn contexts (vessel/sync/route state). Without memo, every TailwindShell
 * re-render re-renders MobileBottomBar even when neither the back handler nor the
 * action list changed — re-creating the live <button> subtree and, on iOS,
 * swapping an in-flight touch target mid-gesture (the first-tap drop this ticket
 * targets). memo short-circuits the parent-driven path: with no props the
 * comparator is trivially equal, so the ONLY thing that can now re-render the bar
 * is a genuine change to `onBack` or `actions` from its own context subscriptions
 * (memo never blocks context-driven updates). Those handlers are already
 * identity-stable via the FT-401 NavigationBackContext / FT-404-part1
 * MobileBottomBarActionsContext stable-wrapper work, so the bar re-renders only on
 * real shape changes.
 */
const MobileBottomBarInner = () => {
  const onBack = useBackHandler();
  const actions = useCurrentBottomBarActions();

  return (
    <footer
      data-role="mobile-bottom-bar"
      data-testid="mobile-bottom-bar"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="mx-auto max-w-screen-md h-16 flex items-stretch">
        <BackSlot onBack={onBack} />
        <MiddleSlots actions={actions} />
        <MenuSlot />
      </div>
    </footer>
  );
};

export const MobileBottomBar = React.memo(MobileBottomBarInner);
MobileBottomBar.displayName = 'MobileBottomBar';

export default MobileBottomBar;
